import {
  CMA_SUMMARY_APARTMENT_TYPE_DATA_ROW,
  CMA_SUMMARY_APARTMENT_TYPE_GROUP_DATA_ROW,
  CMA_SUMMARY_CARE_FEE_DATA_ROW,
  CMA_SUMMARY_CARE_FEE_GROUP_DATA_ROW,
  CMA_SUMMARY_DATA_ROW_TYPE,
  CMA_SUMMARY_LIVING_TYPE_DATA_ROW,
  CMA_SUMMARY_LIVING_TYPE_FEE_DATA_ROW,
  CMA_SUMMARY_MEDICATION_FEE_DATA_ROW,
  CMA_SUMMARY_MEDICATION_FEE_GROUP_DATA_ROW,
  CMA_SUMMARY_TABLE_COLUMNS_OFFSET,
} from './constants';

const slugify = (str: string): string => {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
};

// For values, variant names are concatenated without hyphens (e.g., "studiodeluxe" instead of "studio-deluxe")
const slugifyNoHyphen = (str: string): string => {
  return str.toLowerCase().replace(/[^a-z0-9]+/g, '');
};

const getFeeSlug = (feeLabel: string): string => {
  const feeMapping: Record<string, string> = {
    'Community Fee': 'community-fee',
    'Second Person Mth. Fee': '2nd-person-mth-fee',
    'Pet Fee': 'pet-fee',
    'Parking Fee': 'parking-fee',
    'Respite Fee': 'respite-fee',
    'Additional Fee': 'additional-fee',
    'Move In Fee': 'move-in-fee',
    '2nd Person Entrance Fee': '2nd-person-entrance-fee',
    Deposit: 'deposit',
    'Emergency Pendant Fee': 'em-pendant-fee',
  };
  return feeMapping[feeLabel] || slugify(feeLabel);
};

export const generateLabelDataTestId = (rowMetadata: any): string | undefined => {
  const { type, title, subtitle } = rowMetadata;

  switch (type) {
    case CMA_SUMMARY_DATA_ROW_TYPE.ADDRESS:
      return 'address-label-cma';
    case CMA_SUMMARY_DATA_ROW_TYPE.DISTANCE:
      return 'distance-label-cma';
    case CMA_SUMMARY_DATA_ROW_TYPE.WEBSITE:
      return 'website-label-cma';
    case CMA_SUMMARY_DATA_ROW_TYPE.PHONE:
      return 'phone-label-cma';
    case CMA_SUMMARY_DATA_ROW_TYPE.YEAR_OPENED:
      return 'year-label-cma';
    case CMA_SUMMARY_DATA_ROW_TYPE.NOTES:
      return 'comm-notes-label-cma';
    case CMA_SUMMARY_DATA_ROW_TYPE.SHOP_DATE:
      return 'shop-date-label-cma';
    case CMA_SUMMARY_DATA_ROW_TYPE.REVIEWS:
      return 'reviews-label-cma'; // subtitle gets 'reviews-label-sources-cma' via the subtitle special handling
    case CMA_SUMMARY_DATA_ROW_TYPE.CARE_TYPES:
      return 'care-types-label-cma';
    case CMA_SUMMARY_DATA_ROW_TYPE.OCCUPANCY:
      return 'occupancy-label-cma'; // subtitle gets 'occupancy-overall-label-cma' via the subtitle special handling
    case CMA_SUMMARY_DATA_ROW_TYPE.INCENTIVES:
      return 'incentives-label';
    case CMA_SUMMARY_LIVING_TYPE_DATA_ROW: {
      // Living type row with occupancy (e.g., "Independent Living" with subtitle "Occupancy")
      const livingTypeSlug = slugify(title || '');
      if (subtitle === 'Occupancy') {
        return `${livingTypeSlug}-occupancy-label-cma`;
      }
      return `${livingTypeSlug}-label-cma`;
    }
    case CMA_SUMMARY_LIVING_TYPE_FEE_DATA_ROW: {
      // Fee rows under living type (e.g., "Community Fee" with subtitle "Independent Living")
      const livingTypeSlug = slugify(subtitle || '');
      const feeSlug = getFeeSlug(title || '');
      return `${livingTypeSlug}-${feeSlug}-label`;
    }
    case CMA_SUMMARY_APARTMENT_TYPE_GROUP_DATA_ROW: {
      // Room type group (e.g., "Studio" with subtitle "Independent Living")
      const livingTypeSlug = slugify(subtitle || '');
      const roomTypeSlug = slugify(title || '');
      return `${livingTypeSlug}-room-type-${roomTypeSlug}`;
    }
    case CMA_SUMMARY_APARTMENT_TYPE_DATA_ROW: {
      // Room type variant (e.g., "Studio Deluxe" with subtitle "Independent Living")
      const livingTypeSlug = slugify(subtitle || '');
      const variantSlug = slugify(title || '');
      return `${livingTypeSlug}-room-type-${variantSlug.split('-')[0]}-variant-${variantSlug}`;
    }
    case CMA_SUMMARY_CARE_FEE_GROUP_DATA_ROW: {
      // Care Fees group
      const livingTypeSlug = slugify(subtitle || '');
      return `${livingTypeSlug}-care-fees-label`;
    }
    case CMA_SUMMARY_MEDICATION_FEE_GROUP_DATA_ROW: {
      // Medication Fees group
      const livingTypeSlug = slugify(subtitle || '');
      return `${livingTypeSlug}-medication-fees-label`;
    }
    case CMA_SUMMARY_CARE_FEE_DATA_ROW:
    case CMA_SUMMARY_MEDICATION_FEE_DATA_ROW: {
      // Fee level (e.g., "Level 1")
      const livingTypeSlug = slugify(rowMetadata.living_type || '');
      const levelSlug = slugify(title || '');
      return `${livingTypeSlug}-${levelSlug}-label`;
    }
    default:
      return undefined;
  }
};

// Helper functions for deriving related data-test-ids from a base value
export const getSecondaryValueTestId = (baseTestId: string | undefined, suffix: string): string | undefined => {
  if (!baseTestId) return undefined;
  return baseTestId.replace('-value', `-${suffix}-value`);
};

export const getSqftTestId = (baseTestId: string | undefined): string | undefined => {
  return getSecondaryValueTestId(baseTestId, 'sqft');
};

export const getSqftDiffTestId = (baseTestId: string | undefined): string | undefined => {
  return getSecondaryValueTestId(baseTestId, 'sqft-diff');
};

export const getFeePerSqftTestId = (baseTestId: string | undefined): string | undefined => {
  return getSecondaryValueTestId(baseTestId, 'fee-per-sqft');
};

export const getFeePerSqftDiffTestId = (baseTestId: string | undefined): string | undefined => {
  return getSecondaryValueTestId(baseTestId, 'fee-per-sqft-diff');
};

export const getFeeDiffTestId = (baseTestId: string | undefined): string | undefined => {
  return getSecondaryValueTestId(baseTestId, 'fee-diff');
};

export const getFeeInfoTestId = (baseTestId: string | undefined): string | undefined => {
  if (!baseTestId) return undefined;
  return baseTestId.replace('-value', '-info');
};

export const getCityStateZipTestId = (baseTestId: string | undefined): string | undefined => {
  if (!baseTestId) return undefined;
  return baseTestId.replace('-address-', '-city-state-zip-');
};

export const getNumberOfReviewsTestId = (baseTestId: string | undefined): string | undefined => {
  if (!baseTestId) return undefined;
  return baseTestId.replace('-reviews-score-', '-number-of-reviews-');
};

export const getOccupancy2TestId = (baseTestId: string | undefined): string | undefined => {
  return getSecondaryValueTestId(baseTestId, '2');
};

// Column header test IDs
export const getCommunityNameTestId = (competitorIndex?: number): string => {
  return competitorIndex !== undefined ? `competitor-${competitorIndex}-name` : 'my-community-name';
};

// Incentive test IDs
export const getIncentiveTestId = (incentiveIndex: number, competitorIndex?: number): string => {
  return competitorIndex !== undefined
    ? `incentive-${incentiveIndex}-competitor-${competitorIndex}`
    : `incentive-${incentiveIndex}-my-community`;
};

export const getIncentiveDateAndCareTypeTestId = (incentiveTestId: string): string => {
  return `${incentiveTestId}-date-and-care-type`;
};

// Label test ID constants
export const REVIEWS_LABEL_TEST_ID = 'reviews-label-cma';
export const REVIEWS_SOURCES_LABEL_TEST_ID = 'reviews-label-sources-cma';
export const OCCUPANCY_LABEL_TEST_ID = 'occupancy-label-cma';
export const OCCUPANCY_OVERALL_LABEL_TEST_ID = 'occupancy-overall-label-cma';
export const INCENTIVES_LABEL_TEST_ID = 'incentives-label';
export const INCENTIVES_SECONDARY_LABEL_TEST_ID = 'incentives-secondary-label';

// Label subtitle test ID helper
export const getSubtitleTestId = (dataTestId: string | undefined): string | undefined => {
  if (!dataTestId) return undefined;

  switch (dataTestId) {
    case REVIEWS_LABEL_TEST_ID:
      return REVIEWS_SOURCES_LABEL_TEST_ID;
    case OCCUPANCY_LABEL_TEST_ID:
      return OCCUPANCY_OVERALL_LABEL_TEST_ID;
    case INCENTIVES_LABEL_TEST_ID:
      return INCENTIVES_SECONDARY_LABEL_TEST_ID;
    default:
      return `${dataTestId}-subtitle`;
  }
};

// Unit name test ID helper
export const getUnitNameTestId = (dataTestId: string | undefined): string | undefined => {
  if (!dataTestId) return undefined;
  return `${dataTestId}-unit-name`;
};

export type ColumnInfo = {
  columnType: 'my-community' | 'competitor' | 'competitor-average';
  competitorIndex: number | undefined;
};

export const getColumnInfo = (index: number, hasCompetitorAverageColumn = false): ColumnInfo => {
  const isMyCommunity = index === 1;
  const isCompetitorAverage = hasCompetitorAverageColumn && index === CMA_SUMMARY_TABLE_COLUMNS_OFFSET;

  if (isCompetitorAverage) {
    return {
      columnType: 'competitor-average',
      competitorIndex: undefined,
    };
  }

  const competitorColumnOffset = CMA_SUMMARY_TABLE_COLUMNS_OFFSET + (hasCompetitorAverageColumn ? 1 : 0);

  return {
    columnType: isMyCommunity ? 'my-community' : 'competitor',
    competitorIndex: isMyCommunity ? undefined : index - competitorColumnOffset,
  };
};

export const getValueDataTestId = (
  rowMetadata: any,
  index: number,
  hasCompetitorAverageColumn = false
): string | undefined => {
  const { columnType, competitorIndex } = getColumnInfo(index, hasCompetitorAverageColumn);
  return generateValueDataTestId(rowMetadata, columnType, competitorIndex);
};

export const generateValueDataTestId = (
  rowMetadata: any,
  columnType: 'my-community' | 'competitor' | 'competitor-average',
  competitorIndex?: number
): string | undefined => {
  const { type, title, subtitle, titleMeta } = rowMetadata;
  const prefix =
    columnType === 'my-community'
      ? 'my-community'
      : columnType === 'competitor-average'
        ? 'competitor-average'
        : `competitor-${competitorIndex}`;

  switch (type) {
    case CMA_SUMMARY_DATA_ROW_TYPE.COMMUNITY_NAME:
      if (columnType === 'my-community') {
        return 'my-community-name';
      }
      return columnType === 'competitor-average' ? 'competitor-average-name' : `competitor-${competitorIndex}-name`;
    case CMA_SUMMARY_DATA_ROW_TYPE.ADDRESS:
      return `${prefix}-address-value`;
    case CMA_SUMMARY_DATA_ROW_TYPE.DISTANCE:
      return `${prefix}-distance-value`;
    case CMA_SUMMARY_DATA_ROW_TYPE.WEBSITE:
      return `${prefix}-website-value`;
    case CMA_SUMMARY_DATA_ROW_TYPE.PHONE:
      return `${prefix}-phone-value`;
    case CMA_SUMMARY_DATA_ROW_TYPE.YEAR_OPENED:
      return `${prefix}-year-opened-value`;
    case CMA_SUMMARY_DATA_ROW_TYPE.NOTES:
      return `${prefix}-comm-notes-value`;
    case CMA_SUMMARY_DATA_ROW_TYPE.SHOP_DATE:
      return `${prefix}-shop-date-value`;
    case CMA_SUMMARY_DATA_ROW_TYPE.REVIEWS:
      return `${prefix}-reviews-score-value`;
    case CMA_SUMMARY_DATA_ROW_TYPE.CARE_TYPES:
      return `${prefix}-care-types-value`;
    case CMA_SUMMARY_DATA_ROW_TYPE.OCCUPANCY:
      return `${prefix}-occupancy-overall-value`;
    case CMA_SUMMARY_DATA_ROW_TYPE.INCENTIVES: {
      // Incentives values use different indexing
      return `${prefix}-incentives-value`;
    }
    case CMA_SUMMARY_LIVING_TYPE_DATA_ROW: {
      // Living type occupancy value
      const livingTypeSlug = slugify(title || '');
      return `${prefix}-${livingTypeSlug}-occupancy-value`;
    }
    case CMA_SUMMARY_LIVING_TYPE_FEE_DATA_ROW: {
      // Fee value under living type
      const livingTypeSlug = slugify(subtitle || '');
      const feeSlug = getFeeSlug(title || '');
      return `${prefix}-${livingTypeSlug}-${feeSlug}-value`;
    }
    case CMA_SUMMARY_APARTMENT_TYPE_GROUP_DATA_ROW: {
      // Room type group value
      const livingTypeSlug = slugify(subtitle || '');
      const roomTypeSlug = slugify(title || '');
      return `${prefix}-${livingTypeSlug}-room-type-${roomTypeSlug}-value`;
    }
    case CMA_SUMMARY_APARTMENT_TYPE_DATA_ROW: {
      // Room type variant value - variant names are concatenated without hyphens
      const livingTypeSlug = slugify(subtitle || '');
      const variantSlug = slugifyNoHyphen(title || '');
      return `${prefix}-${livingTypeSlug}-room-variant-${variantSlug}-value`;
    }
    case CMA_SUMMARY_CARE_FEE_GROUP_DATA_ROW: {
      // Care Fees group value
      const livingTypeSlug = slugify(subtitle || '');
      return `${prefix}-${livingTypeSlug}-care-fees-value`;
    }
    case CMA_SUMMARY_MEDICATION_FEE_GROUP_DATA_ROW: {
      // Medication Fees group value
      const livingTypeSlug = slugify(subtitle || '');
      return `${prefix}-${livingTypeSlug}-medication-fees-value`;
    }
    case CMA_SUMMARY_CARE_FEE_DATA_ROW:
    case CMA_SUMMARY_MEDICATION_FEE_DATA_ROW: {
      // Fee level value
      const livingTypeSlug = slugify(rowMetadata.living_type || '');
      const levelSlug = slugify(title || '');
      return `${prefix}-${livingTypeSlug}-${levelSlug}-value`;
    }
    default:
      return undefined;
  }
};
